import { PUSD_CONTRACT_ADDRESS, USDC_E_CONTRACT_ADDRESS } from '@/constants/tokens'
import type { PolymarketActiveWallet } from '@/utils/polymarket-wallet-address'
import type { Address, PublicClient } from 'viem'

const erc20BalanceAbi = [
	{
		name: 'balanceOf',
		type: 'function',
		stateMutability: 'view',
		inputs: [{ name: 'account', type: 'address' }],
		outputs: [{ name: '', type: 'uint256' }],
	},
] as const

const POLYMARKET_DATA_API = 'https://data-api.polymarket.com'

/**
 * Auto-pick the default active wallet for a legacy Safe user who has no
 * stored preference yet.
 *
 * Rules (in priority order):
 *   1. Safe not deployed on-chain → 'deposit'. The Safe was never usable and
 *      deploying it now would be rejected by Polymarket (post-V2-cutover).
 *   2. Safe has open positions/orders → 'safe'. Cash comparison must never
 *      pull a user away from live positions (outcome tokens can't move).
 *   3. Higher combined cash (pUSD + USDC.e) wins; ties stay on 'safe'
 *      (legacy default, zero behavior change).
 */
export function pickDefaultActiveWallet(params: {
	safeDeployed: boolean
	safeHasOpenPositions: boolean
	safeCashRaw: bigint
	depositCashRaw: bigint
}): PolymarketActiveWallet {
	if (!params.safeDeployed) return 'deposit'
	if (params.safeHasOpenPositions) return 'safe'
	return params.depositCashRaw > params.safeCashRaw ? 'deposit' : 'safe'
}

/**
 * Combined pUSD + USDC.e balance (both 6 decimals) for cash comparison
 * between the Safe and the deposit wallet.
 */
export async function readCombinedCashRaw(
	publicClient: PublicClient,
	address: Address
): Promise<bigint> {
	const [pusd, usdce] = await Promise.all([
		publicClient.readContract({
			address: PUSD_CONTRACT_ADDRESS as Address,
			abi: erc20BalanceAbi,
			functionName: 'balanceOf',
			args: [address],
		}),
		publicClient.readContract({
			address: USDC_E_CONTRACT_ADDRESS as Address,
			abi: erc20BalanceAbi,
			functionName: 'balanceOf',
			args: [address],
		}),
	])
	return pusd + usdce
}

/**
 * Count of non-redeemable open positions on an address via the Polymarket
 * data API. Fail-open to 1 on error: an unknown position state must block
 * auto-switching away from the Safe (rule 2 above), never enable it.
 */
export async function fetchOpenPositionCount(address: string): Promise<number> {
	try {
		const params = new URLSearchParams({ user: address, sizeThreshold: '0.01', limit: '500' })
		const response = await fetch(`${POLYMARKET_DATA_API}/positions?${params}`)
		if (!response.ok) return 1
		const positions = (await response.json()) as { redeemable: boolean }[]
		return positions.filter(p => !p.redeemable).length
	} catch {
		return 1
	}
}

/**
 * Persist the active-wallet preference server-side so withdraw destinations
 * and every resolvePolymarketWalletAddress call site follow the switch.
 * Throws on non-OK so callers can surface/rollback.
 */
export async function persistActiveWalletPreference(
	activeWallet: PolymarketActiveWallet
): Promise<void> {
	const { authFetch } = await import('@/lib/auth/auth-fetch')
	const res = await authFetch('/api/user/polymarket-wallet', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ activeWallet }),
	})
	if (!res.ok) {
		const body = (await res.json().catch(() => ({}))) as { error?: string }
		throw new Error(body.error ?? `Failed to save wallet preference (${res.status})`)
	}
}
